const MAX_IMAGE_DIMENSION = 1200;
const MAX_IMAGE_BYTES = 1_048_576;
const IMAGE_QUALITY = 0.9;

export const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Falha ao carregar imagem para otimização"));
    image.src = src;
  });

export const needsImageOptimization = (
  sizeInBytes: number,
  width: number,
  height: number,
) =>
  sizeInBytes > MAX_IMAGE_BYTES ||
  width > MAX_IMAGE_DIMENSION ||
  height > MAX_IMAGE_DIMENSION;

const imageToOptimizedBlob = async (image: HTMLImageElement): Promise<Blob> => {
  const naturalWidth = image.naturalWidth || image.width || 1;
  const naturalHeight = image.naturalHeight || image.height || 1;
  const scale = Math.min(
    MAX_IMAGE_DIMENSION / naturalWidth,
    MAX_IMAGE_DIMENSION / naturalHeight,
    1,
  );
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas não disponível para otimização");
  }
  context.drawImage(image, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error("Falha ao gerar imagem otimizada"));
        }
      },
      "image/jpeg",
      IMAGE_QUALITY,
    );
  });
};

export const createOptimizedPreviewFile = async (
  image: HTMLImageElement,
  photoKey: string,
  variant: "original" | "edited" = "original",
): Promise<File> => {
  const blob = await imageToOptimizedBlob(image);
  const fileName =
    variant === "edited" ? `${photoKey}-edited.jpg` : `${photoKey}-original.jpg`;
  return new File([blob], fileName, { type: "image/jpeg" });
};

/** Mesma compressão das fotos do assessment: máx. 1200px e ~1MB, JPEG 0.9. */
export const optimizeImageFile = async (
  file: File,
  outputFileName = "avatar.jpg",
): Promise<File> => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;

    if (!needsImageOptimization(file.size, naturalWidth, naturalHeight)) {
      return file;
    }

    const blob = await imageToOptimizedBlob(image);
    return new File([blob], outputFileName, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
